import { useState, useMemo, type ReactNode } from 'react';
import { useApi, Cargando, ErrorApi, Vacio, formatFecha, formatU } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';

interface Servicio {
  convocatoria_id: string;
  fecha: string;
  estado: string;
  salon_id: string;
  salon_nombre: string;
  persona_nombre: string;
  persona_apellido: string;
  categoria_nombre: string;
  hora_prevista_inicio: string;
  hora_prevista_fin: string;
  llegada_en: string | null;
  salida_en: string | null;
  hora_inicio_real: string | null;
  hora_fin_real: string | null;
  horas: number;
  cobro: number | null;
  estimado: boolean;
}

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function getMesKey(fecha: string): string {
  return fecha.slice(0, 7); // "2026-07"
}

function getMesLabel(key: string): string {
  const [anio, m] = key.split('-');
  return `${MESES[parseInt(m, 10) - 1]} ${anio}`;
}

/** Lunes de la semana de una fecha YYYY-MM-DD, como clave YYYY-MM-DD. */
function getSemanaKey(fecha: string): string {
  const f = new Date(`${fecha}T00:00:00`);
  const dow = f.getDay() === 0 ? 7 : f.getDay();
  f.setDate(f.getDate() - (dow - 1));
  return f.toISOString().slice(0, 10);
}

function getSemanaLabel(lunesKey: string): string {
  const lunes = new Date(`${lunesKey}T00:00:00`);
  const domingo = new Date(lunes);
  domingo.setDate(domingo.getDate() + 6);
  const corta = (d: Date) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  return `Semana del ${corta(lunes)} al ${corta(domingo)}`;
}

function horaLocal(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function diaCorto(fecha: string): string {
  return new Date(`${fecha}T00:00:00`)
    .toLocaleDateString('es-UY', { weekday: 'short' })
    .replace('.', '');
}

function sumarCobros(items: Servicio[]): { total: number; hayEstimados: boolean } {
  let total = 0;
  let hayEstimados = false;
  for (const s of items) {
    if (s.cobro != null) total += s.cobro;
    if (s.estimado && s.cobro != null) hayEstimados = true;
  }
  return { total, hayEstimados };
}

export function ServiciosPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<{ servicios: Servicio[] }>('/api/cliente/servicios');
  const [filtroSalon, setFiltroSalon] = useState('');
  const [mesAbierto, setMesAbierto] = useState<string | null>(null);

  const servicios = useMemo(() => {
    const todos = datos?.servicios ?? [];
    return filtroSalon ? todos.filter((s) => s.salon_id === filtroSalon) : todos;
  }, [datos, filtroSalon]);

  const salones = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of datos?.servicios ?? []) m.set(s.salon_id, s.salon_nombre);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [datos]);

  // mes → semana → servicios (ya vienen ordenados por fecha DESC)
  const meses = useMemo(() => {
    const porMes = new Map<string, Map<string, Servicio[]>>();
    for (const s of servicios) {
      const mk = getMesKey(s.fecha);
      if (!porMes.has(mk)) porMes.set(mk, new Map());
      const semanas = porMes.get(mk)!;
      const sk = getSemanaKey(s.fecha);
      if (!semanas.has(sk)) semanas.set(sk, []);
      semanas.get(sk)!.push(s);
    }
    return [...porMes.entries()];
  }, [servicios]);

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;
  if (!datos || datos.servicios.length === 0) {
    return <Vacio texto="Todavía no hay servicios realizados. Cuando se hagan eventos con tu personal, acá vas a ver quién vino, sus horarios y el costo." />;
  }

  const abierto = mesAbierto ?? meses[0]?.[0] ?? null;
  const hayEstimadosGlobal = servicios.some((s) => s.estimado && s.cobro != null);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-neutro-800">Servicios realizados</h2>
        <p className="text-sm text-neutro-500 mt-0.5">Quién vino a cada evento, sus horarios y el costo</p>
      </div>

      <select className="campo max-w-xs" value={filtroSalon} onChange={(e) => setFiltroSalon(e.target.value)}>
        <option value="">Todos los salones</option>
        {salones.map(([id, nombre]) => (
          <option key={id} value={id}>{nombre}</option>
        ))}
      </select>

      {meses.length === 0 && <Vacio texto="Nada con este filtro." />}

      {meses.map(([mesKey, semanas]) => {
        const itemsMes = [...semanas.values()].flat();
        const { total: totalMes, hayEstimados } = sumarCobros(itemsMes);
        const estaAbierto = abierto === mesKey;
        return (
          <section key={mesKey} className="rounded-2xl border border-contorno-var bg-white overflow-hidden">
            <button
              type="button"
              className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-superficie-100/60 transition-colors"
              onClick={() => setMesAbierto(estaAbierto ? '' : mesKey)}
            >
              <div>
                <p className="font-bold text-neutro-800">{getMesLabel(mesKey)}</p>
                <p className="text-xs text-neutro-400">{itemsMes.length} servicio{itemsMes.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-primario-500">{hayEstimados ? '≈ ' : ''}{formatU(totalMes)}</p>
                <p className="text-xs text-neutro-400">{estaAbierto ? 'Ocultar ▲' : 'Ver detalle ▼'}</p>
              </div>
            </button>

            {estaAbierto && (
              <div className="border-t border-contorno-var px-4 pb-4">
                {[...semanas.entries()].map(([semKey, items]) => {
                  const { total: totalSem, hayEstimados: estSem } = sumarCobros(items);
                  return (
                    <div key={semKey} className="mt-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-primario-400">{getSemanaLabel(semKey)}</h4>
                        <span className="text-xs font-semibold text-neutro-500">{estSem ? '≈ ' : ''}{formatU(totalSem)}</span>
                      </div>
                      <ul className="mt-2 space-y-2">
                        {items.map((s) => (
                          <FilaServicio key={s.convocatoria_id} s={s} />
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}

      {hayEstimadosGlobal && (
        <p className="text-xs text-neutro-400">
          ≈ Monto estimado según horario previsto y tarifa vigente: la empresa aún no cargó el turno real de ese servicio.
        </p>
      )}
    </div>
  );
}

function FilaServicio({ s }: { s: Servicio }): ReactNode {
  const llegada = horaLocal(s.llegada_en);
  const salida = horaLocal(s.salida_en);
  const noShow = s.estado === 'no_show';
  return (
    <li className="rounded-xl border border-contorno-var bg-superficie-100/40 px-3.5 py-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-neutro-800 capitalize">
            {diaCorto(s.fecha)} {formatFecha(s.fecha)}
            <span className="ml-2 font-normal text-neutro-400">{s.salon_nombre}</span>
          </p>
          <p className="mt-0.5 font-medium">
            {s.persona_apellido}, {s.persona_nombre}
            <span className="ml-1.5 text-xs font-semibold text-primario-400">{s.categoria_nombre}</span>
          </p>
        </div>
        <div className="shrink-0 text-right">
          {noShow ? (
            <span className="badge badge-error">No se presentó</span>
          ) : s.cobro != null ? (
            <>
              <p className="font-bold text-neutro-800">{s.estimado ? '≈ ' : ''}{formatU(s.cobro)}</p>
              <p className="text-xs text-neutro-400">{s.horas.toLocaleString('es-UY')} h</p>
            </>
          ) : (
            <p className="text-xs text-neutro-400">Sin tarifa</p>
          )}
        </div>
      </div>
      {!noShow && (
        <p className="mt-1.5 text-xs text-neutro-500">
          Previsto {formatoRango(s.hora_prevista_inicio, s.hora_prevista_fin)}
          {' · '}
          {llegada ? (
            <span className="font-semibold text-estado-ok">Entrada ✓ {llegada}</span>
          ) : (
            <span className="text-neutro-400">No marcó entrada</span>
          )}
          {' · '}
          {salida ? (
            <span className="font-semibold text-estado-ok">Salida ✓ {salida}</span>
          ) : (
            <span className="text-neutro-400">Sin salida</span>
          )}
          {s.hora_inicio_real && s.hora_fin_real && (
            <span className="text-neutro-400"> · Turno cargado {s.hora_inicio_real}–{s.hora_fin_real}</span>
          )}
        </p>
      )}
    </li>
  );
}
